import { animate } from "motion";
import { commitMediaStackState, crossfadeMedia, pauseVideosIn } from "../lib/media-crossfade.js";

const MD_BREAKPOINT = 768;

class HeroCardSection extends HTMLElement {
  connectedCallback() {
    this.activeIndex = parseInt(this.dataset.activeIndex, 10) || 0;
    this.cardCount = parseInt(this.dataset.cardCount, 10) || 0;

    if (this.cardCount === 0) return;

    this.mediaGallery = this.querySelector("[data-hero-gallery]");
    this.mediaPanels = this.mediaGallery ? [...this.mediaGallery.querySelectorAll("[data-hero-image]")] : [];
    this.thumbnails = [...this.querySelectorAll("[data-thumbnail]")];
    this.headlines = [...this.querySelectorAll("[data-headline]")];
    this.subheadlines = [...this.querySelectorAll("[data-subheadline]")];
    this.descriptions = [...this.querySelectorAll("[data-description]")];
    this.primaryCtas = [...this.querySelectorAll("[data-primary-cta]")];
    this.primaryCtaFallback = this.dataset.primaryCtaFallback || "";

    this.isTransitioning = false;
    this.desktopQuery = window.matchMedia(`(min-width: ${MD_BREAKPOINT}px)`);
    this._transitionGen = 0;

    this.initTextPositions(this.headlines);
    this.initTextPositions(this.subheadlines);
    commitMediaStackState(this.mediaPanels, this.activeIndex);
    this.setMediaPanelAria(this.activeIndex);
    this.updatePrimaryCtaHref(this.activeIndex);
    this.playActivePanelVideo(this.activeIndex);

    this.bindThumbnails();
    this.bindMediaChange();
    this.#waitForEntrance();
  }

  disconnectedCallback() {
    this.desktopQuery?.removeEventListener("change", this.handleMediaChange);
  }

  initTextPositions(elements) {
    elements.forEach((el, i) => {
      if (i === this.activeIndex) {
        el.style.position = "relative";
        el.style.inset = "";
        el.style.opacity = "1";
      } else {
        el.style.position = "absolute";
        el.style.inset = "0";
        el.style.opacity = "0";
      }
    });
  }

  activate(index) {
    if (index === this.activeIndex || index < 0 || index >= this.cardCount || this.isTransitioning) return;

    const prevIndex = this.activeIndex;
    const direction = index > prevIndex ? 1 : -1;

    this.isTransitioning = true;
    this.activeIndex = index;
    this.dataset.activeIndex = String(index);

    this.thumbnails.forEach((thumb, i) => {
      thumb.setAttribute("aria-selected", i === index ? "true" : "false");
    });

    this.showActiveDescription(index);
    this.updatePrimaryCtaHref(index);

    const seq = crossfadeMedia(this.mediaPanels, prevIndex, index, direction, () => {
      this.setMediaPanelAria(index);
      this.playActivePanelVideo(index);
      this.isTransitioning = false;
    });

    if (!seq) {
      this.isTransitioning = false;
    }

    const transitionGen = ++this._transitionGen;
    const textTransitionPromises = [
      this.animateTextTransition(this.headlines, prevIndex, index),
      this.subheadlines.length > 0 ? this.animateTextTransition(this.subheadlines, prevIndex, index) : null
    ].filter(Boolean);

    Promise.all(textTransitionPromises).finally(() => {
      if (transitionGen !== this._transitionGen) return;

      this.commitTextStackState(this.headlines, index);
      if (this.subheadlines.length > 0) {
        this.commitTextStackState(this.subheadlines, index);
      }
    });
  }

  commitTextStackState(elements, activeIndex) {
    const isHeadline = elements === this.headlines;

    elements.forEach((el, i) => {
      el.getAnimations().forEach((a) => a.cancel());
      if (i === activeIndex) {
        el.style.position = "relative";
        el.style.inset = "";
        el.style.opacity = "1";
        el.removeAttribute("aria-hidden");
      } else {
        el.style.position = "absolute";
        el.style.inset = "0";
        el.style.opacity = "0";
        el.setAttribute("aria-hidden", "true");
      }
    });

    if (isHeadline) {
      this.headlines.forEach((h1, i) => {
        h1.setAttribute("aria-hidden", i === activeIndex ? "false" : "true");
      });
    } else {
      this.subheadlines.forEach((el, i) => {
        el.setAttribute("aria-hidden", i === activeIndex ? "false" : "true");
      });
    }
  }

  animateTextTransition(elements, prevIndex, newIndex) {
    const outgoing = elements[prevIndex];
    const incoming = elements[newIndex];
    if (!outgoing || !incoming) return Promise.resolve();

    const isHeadline = elements === this.headlines;

    elements.forEach((el, i) => {
      if (i !== prevIndex && i !== newIndex) {
        el.getAnimations().forEach((a) => a.cancel());
        el.style.position = "absolute";
        el.style.inset = "0";
        el.style.opacity = "0";
        el.setAttribute("aria-hidden", "true");
      }
    });

    const TEXT_EASING = {
      type: "spring",
      stiffness: 250,
      damping: 20,
      mass: 1
    };

    outgoing.getAnimations().forEach((a) => a.cancel());
    outgoing.style.position = "absolute";
    outgoing.style.inset = "0";
    outgoing.setAttribute("aria-hidden", "true");
    const outAnim = animate(outgoing, { opacity: 0 }, { duration: 0.1, ...TEXT_EASING });

    incoming.getAnimations().forEach((a) => a.cancel());
    incoming.style.position = "relative";
    incoming.style.inset = "";
    incoming.style.opacity = "0";
    incoming.removeAttribute("aria-hidden");
    const inAnim = animate(incoming, { opacity: 1 }, { duration: 0.1, ...TEXT_EASING });

    if (isHeadline) {
      this.headlines.forEach((h1, i) => {
        h1.setAttribute("aria-hidden", i === newIndex ? "false" : "true");
      });
    } else {
      this.subheadlines.forEach((el, i) => {
        el.setAttribute("aria-hidden", i === newIndex ? "false" : "true");
      });
    }

    return Promise.all([outAnim.finished, inAnim.finished]);
  }

  showActiveDescription(index) {
    this.descriptions.forEach((el) => {
      const i = parseInt(el.dataset.description, 10);
      el.classList.toggle("hidden", i !== index);
      el.classList.toggle("block", i === index);
    });
  }

  updatePrimaryCtaHref(index) {
    if (this.primaryCtas.length === 0) return;

    const thumb = this.thumbnails[index];
    const productUrl = thumb?.dataset.productUrl;
    const href = productUrl || this.primaryCtaFallback;

    if (href) {
      this.primaryCtas.forEach((cta) => {
        cta.setAttribute("href", href);
      });
    }
  }

  bindThumbnails() {
    this.thumbnails.forEach((thumb) => {
      const index = parseInt(thumb.dataset.thumbnail, 10);

      thumb.addEventListener("click", (e) => {
        e.preventDefault();
        this.activate(index);
      });
    });
  }

  setMediaPanelAria(activeIndex) {
    this.mediaPanels.forEach((panel, i) => {
      if (i === activeIndex) {
        panel.removeAttribute("aria-hidden");
      } else {
        panel.setAttribute("aria-hidden", "true");
      }
    });
  }

  bindMediaChange() {
    this.handleMediaChange = () => {
      this.updatePrimaryCtaHref(this.activeIndex);
      commitMediaStackState(this.mediaPanels, this.activeIndex);
      this.setMediaPanelAria(this.activeIndex);
      this.playActivePanelVideo(this.activeIndex);
    };
    this.desktopQuery.addEventListener("change", this.handleMediaChange);
  }

  playActivePanelVideo(index) {
    pauseVideosIn(this);
    const panel = this.mediaPanels[index];
    const video = panel?.querySelector("video");
    if (video) video.play().catch(() => {});
  }

  isDesktop() {
    return this.desktopQuery?.matches ?? false;
  }

  #waitForEntrance() {
    const html = document.documentElement;

    if (html.classList.contains("manifest-overlay-skipped")) return;
    if (!html.classList.contains("manifest-overlay-active")) return;

    this.dataset.awaitingEntrance = "";

    const header = document.querySelector("#header-component");
    if (header) header.dataset.awaitingEntrance = "";

    window.addEventListener(
      "manifest-overlay:complete",
      () => {
        this.#playEntrance();
      },
      { once: true }
    );
  }

  #playEntrance() {
    const taglines = this.querySelector('[data-hero-entrance="taglines"]');
    const image = this.querySelector('[data-hero-entrance="image"]');
    const bottoms = [...this.querySelectorAll('[data-hero-entrance="bottom"]')];
    const header = document.querySelector("#header-component");

    const ease = [0.22, 1, 0.36, 1];
    const duration = 1.2;

    const cleanup = () => {
      delete this.dataset.awaitingEntrance;
      if (header) {
        delete header.dataset.awaitingEntrance;
        header.style.opacity = "";
        header.style.transform = "";
        header.style.pointerEvents = "";
        header.style.transition = "";
      }
    };

    if (header) {
      header.style.transition = "none";
      animate(header, { opacity: [0, 1], y: [-48, 0] }, { duration, ease });
    }
    if (taglines) {
      animate(taglines, { opacity: [0, 1], y: [-48, 0] }, { duration, ease });
    }
    if (image) {
      animate(image, { opacity: [0, 1] }, { duration, ease });
    }
    const last = bottoms.map((el) => animate(el, { opacity: [0, 1], y: [48, 0] }, { duration, ease }));
    const all = last.length ? last[last.length - 1].finished : Promise.resolve();
    all.then(cleanup);
  }
}

if (!customElements.get("hero-card-section")) {
  customElements.define("hero-card-section", HeroCardSection);
}
